/**
 * Authed bootstrap Hook
 *
 * 登录态进入后台 Layout（首次登录、F5 刷新、切换账号）时，
 * 一次性预拉账号无关 / 账号相关的常驻数据：
 *   - settings.alertEmail（user-scoped）
 *   - subscription plans（全局只读）
 *   - current subscription（user-scoped，供 Recharge / Dashboard 等即时渲染当前计划）
 *
 * 与 `useAutoRefreshOverview` 不同，这里不做轮询；页面内路由切换
 * 不会重新触发，避免每次进入 Dashboard / Recharge 都重发请求。
 */

import { useEffect } from 'react';
import { useIsAuthenticated, useUserInfo } from '@/stores/modules/auth';
import { useRefreshSettings } from '@/stores/modules/settings';
import { useRefreshSubscriptionPlans } from '@/stores/modules/plans';
import { useRefreshCurrentSubscription } from '@/stores/modules/currentSubscription';

/** 进入登录态后延迟拉取，避开 setAuth 同帧的重渲染 */
const BOOTSTRAP_DELAY_MS = 100;

/**
 * 登录后一次性预热账号常驻数据。
 *
 * 触发条件：`isAuthenticated && userInfo.userId && pathname !== '/login'`。
 * 依赖 `userId` 变化，因此切换账号会再次触发。
 */
export const useAuthedBootstrap = (): void => {
  const isAuthenticated = useIsAuthenticated();
  const userInfo = useUserInfo();
  const refreshSettings = useRefreshSettings();
  const refreshPlans = useRefreshSubscriptionPlans();
  const refreshCurrentSubscription = useRefreshCurrentSubscription();

  useEffect(() => {
    if (
      !isAuthenticated ||
      !userInfo?.userId ||
      window.location.pathname === '/login'
    ) {
      return;
    }

    const timer = setTimeout(() => {
      void refreshSettings();
      void refreshPlans();
      void refreshCurrentSubscription();
    }, BOOTSTRAP_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [
    isAuthenticated,
    userInfo?.userId,
    refreshSettings,
    refreshPlans,
    refreshCurrentSubscription,
  ]);
};
